import { faGripVertical } from '@fortawesome/free-solid-svg-icons/faGripVertical';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FlexGrid, Loader, Panel, Text } from '@gasbuddy/react-components';
import { useEffectWithDeepCompare } from '@gasbuddy/react-hooks';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { noop, pickStationBrandIds, priceFormatter } from '../../../lib/utils';
import { getBrandMatchesForStationBrands } from '../../../lib/utils/station';
import { KEYNAMES } from '../../constants';
import StationContext from '../../context/station';
import BrandPropTypes from '../../prop-types/brand';
import LatLngPropType from '../../prop-types/latlng';
import PricePropType from '../../prop-types/price';
import StationPropType from '../../prop-types/station';
import BadgeAds from '../BadgeAds';
import EnterpriseBadge from '../EnterpriseBadge';
import FavoriteStationIcon from '../FavoriteStationIcon';
import NoClickPropagation from '../NoClickPropagation';
import OutageStatusBadges from '../OutageStatusBadges';
import ReportedBy from '../ReportedBy';
import StarRating from '../StarRating';
import StationDistance from '../StationDistance';
import StationBrandings from '../StationInfoBox/StationBrandings';
import StationDisplayName from '../StationInfoBox/StationDisplayName';
import VerboseStationInfo from '../VerboseStationInfo';
import styles from './GenericStationListItem.module.css';

const cx = classnames.bind(styles);

export default function GenericStationListItem({
  brands,
  displayPrice,
  fetchBrands,
  hasBadgeAds,
  history,
  isFetchingFuels,
  noFavoriting,
  onPriceClicked,
  relativeLocation,
  small,
  station,
  verbose,
}) {
  const visitStation = useCallback((e) => {
    e.preventDefault();

    // Let links handle themselves
    const linkWasClicked = e.target.tagName === 'A';

    if (!linkWasClicked && station.id && (!e.key || [KEYNAMES.ENTER, KEYNAMES.SPACE].includes(e.key))) {
      history.push(`/station/${station.id}`);
    }
  }, [history, station.id]);

  useEffectWithDeepCompare(() => {
    const brandIds = pickStationBrandIds(station);

    if (brandIds.length > brands.length) {
      fetchBrands(brandIds);
    }
  }, [fetchBrands, station, brands.length]);

  const {
    address,
    emergency_status: emergencyStatus,
    enterprise: isEnterprise,
    id: stationId,
    latitude: lat,
    longitude: lng,
    name,
    ratings_count: ratingsCount,
    sellsDiesel,
    star_rating: starRating,
  } = station;

  const stationDisplayPrice = displayPrice || station.displayPrice;
  const isEmergencyStatus = Object.keys(emergencyStatus || {}).length > 0;
  const formattedPrice = priceFormatter(stationDisplayPrice.price || 0, address.country);
  const showDistance = relativeLocation?.lat && relativeLocation?.lng;
  const stationLocation = { lat, lng };
  const stationBrands = getBrandMatchesForStationBrands(station.brandings, brands);

  const priceContainerProps = !noFavoriting && {
    onClick: onPriceClicked,
  };

  const stationDetailsJsx = (
    <React.Fragment>
      <div className={cx('stationListItem')} onClick={visitStation} onKeyDown={visitStation} role="link" tabIndex={0}>
        <div className={cx('logoColumn')}>
          <StationBrandings
            brands={stationBrands}
            stationName={name}
            containerClass={cx('logoImageContainer')}
          />
        </div>

        <div className={cx('mainInfoColumn')}>
          <StationDisplayName
            as="h3"
            brands={stationBrands}
            stationName={name}
            headerClass={cx('stationNameHeader')}
            subheaderClass={cx('stationNameSubHeader')}
            to={`/station/${stationId}`}
            headerBadge={isEnterprise && (
              <NoClickPropagation as="span">
                <EnterpriseBadge />
              </NoClickPropagation>
            )}
          />
          <div className={cx('ratingContainer')}>
            <StarRating
              name={`station${stationId}Rating`}
              rating={starRating}
              size="small"
            />
            &nbsp;
            <Text className={cx('numberOfReviews')}>
              {ratingsCount || 0}
            </Text>
          </div>
          <div className={cx('address')}>
            {address.line_1}
            <br />
            {address.locality && address.region && `${address.locality}, ${address.region}` }
          </div>

          {noFavoriting && !small && isEmergencyStatus && (
            <div className={cx('emergencyStatus')}>
              <OutageStatusBadges
                powerReport={emergencyStatus.has_power && {
                  status: emergencyStatus.has_power.report_status,
                  username: emergencyStatus.has_power.nick_name,
                  timestamp: emergencyStatus.has_power.update_date,
                }}
                gasReport={emergencyStatus.has_gas && {
                  status: emergencyStatus.has_gas.report_status,
                  username: emergencyStatus.has_gas.nick_name,
                  timestamp: emergencyStatus.has_gas.update_date,
                }}
                dieselReport={emergencyStatus.has_diesel && {
                  status: emergencyStatus.has_diesel.report_status,
                  username: emergencyStatus.has_diesel.nick_name,
                  timestamp: emergencyStatus.has_diesel.update_date,
                }}
                sellsDiesel={sellsDiesel}
                iconsOnly
              />
            </div>
          )}

          {noFavoriting && !small && !isEmergencyStatus && hasBadgeAds && (
            <BadgeAds badgeIds={station.badges} />
          )}
        </div>

        <div className={cx('badgeContainer')}>
          {!noFavoriting
            ? <FavoriteStationIcon stationId={stationId} size="2x" />
            : (
              <React.Fragment>
                {showDistance && (
                  <div className={cx('distanceLabel')}>
                    <StationDistance
                      to={stationLocation}
                      from={relativeLocation}
                      country={address.country}
                    />
                  </div>
                )}
              </React.Fragment>
            )
          }
        </div>

        <div className={cx('priceColumn', { clickable: !noFavoriting })} {...priceContainerProps}>
          {isFetchingFuels ? (
            <Loader size="sm" />
          ) : (
            <div className={cx('priceContainer')}>
              <Text className={cx('price')}>
                { /* intentionally no line break */}
                {formattedPrice}
              </Text>

              {stationDisplayPrice.reportedBy && (
                <React.Fragment>
                  <ReportedBy
                    username={stationDisplayPrice.reportedBy}
                    time={stationDisplayPrice.postedTime}
                  />
                  {stationDisplayPrice.isCash && (
                    <Text className={cx('cashTag')}>CASH</Text>
                  )}
                </React.Fragment>
              )}
            </div>
          )}
        </div>
      </div>
      {verbose && <VerboseStationInfo stationId={stationId} />}
    </React.Fragment>
  );

  return (
    <StationContext.Provider value={stationId}>
      <Panel
        className={cx('station', { small: !!small })}
        color="white"
        padded={false}
        bordered={!small}
        rounded
      >
        {!noFavoriting
          ? (
            <FlexGrid container>
              <FlexGrid.Column mobile={1} className={cx('dragIconContainer')}>
                <div className={cx('verticalEllipsisContainer')}>
                  <FontAwesomeIcon icon={faGripVertical} className={cx('verticalEllipsis')} size="2x" />
                </div>
              </FlexGrid.Column>
              <FlexGrid.Column mobile={11} className={cx('stationDetails', 'clickable')}>
                {stationDetailsJsx}
              </FlexGrid.Column>
            </FlexGrid>
          )
          : stationDetailsJsx
        }
      </Panel>
    </StationContext.Provider>
  );
}

GenericStationListItem.propTypes = {
  brands: PropTypes.arrayOf(BrandPropTypes),
  displayPrice: PricePropType,
  fetchBrands: PropTypes.func.isRequired,
  hasBadgeAds: PropTypes.bool,
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
  isFetchingFuels: PropTypes.bool.isRequired,
  noFavoriting: PropTypes.bool,
  onPriceClicked: PropTypes.func,
  relativeLocation: LatLngPropType,
  small: PropTypes.bool,
  station: StationPropType,
  /** whether to show amenities and reviews pop-down */
  verbose: PropTypes.bool,
};

GenericStationListItem.defaultProps = {
  brands: [],
  displayPrice: undefined,
  hasBadgeAds: false,
  history: {
    push: noop,
  },
  noFavoriting: true,
  onPriceClicked: noop,
  relativeLocation: {},
  small: false,
  station: {},
  verbose: false,
};
